'use client';

import type { ListDetail } from '@swishapp/api-client';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useWishlistMutations } from '../../../../wishlist/client/hooks/use-wishlist-mutations';
import {
  ResponsiveDialog,
  ResponsiveDialogClose,
  ResponsiveDialogContent,
  ResponsiveDialogTrigger,
} from '../../../responsive-dialog';
import { Button } from '../../../ui/button';
import { Checkbox } from '../../../ui/checkbox';
import { DialogFooter } from '../../../ui/dialog';
import { useWishlistItemsWithProducts } from './use-wishlist-items-with-products';
import type { WishlistItemWithProduct } from './wishlist-types';

import type { ComponentType } from 'react';
import type {
  SharedWishlistCardData,
  WishlistCardItem,
} from '../../../wishlist-card';

interface AddItemsToBoardDialogProps {
  board: ListDetail;
  onItemsAdded?: () => void;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children?: ReactNode;
  primaryButtonClassName?: string;
  secondaryButtonClassName?: string;
  wishlistCard: ComponentType<{
    product: SharedWishlistCardData;
    item: WishlistCardItem;
    boardId?: string;
    className?: string;
  }>;
}

function SelectableWishlistCard({
  item,
  isSelected,
  onToggle,
  WishlistCard,
}: {
  item: WishlistItemWithProduct;
  isSelected: boolean;
  onToggle: (selected: boolean) => void;
  WishlistCard: ComponentType<{
    product: SharedWishlistCardData;
    item: WishlistCardItem;
    boardId?: string;
    className?: string;
  }>;
}) {
  if (!item.product) {
    return (
      <div className="relative">
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div className="flex flex-col space-y-2">
            <div>
              <div className="font-medium text-gray-600 text-sm">
                Product ID: {item.productId}
              </div>
              <div className="text-gray-500 text-xs">
                Product data unavailable
              </div>
            </div>
          </div>
        </div>
        <div className="absolute top-2 left-2">
          <Checkbox
            id={`item-${item.id}`}
            checked={isSelected}
            onCheckedChange={onToggle}
            className="bg-white shadow-sm"
            aria-label={`Select product: Product ID ${item.productId}`}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <WishlistCard
        product={item.product}
        item={item}
        className={
          isSelected ? 'ring-2 ring-[hsl(var(--color))] ring-offset-1' : ''
        }
      />
      <div className="absolute top-2 left-2">
        <Checkbox
          id={`item-${item.id}`}
          checked={isSelected}
          onCheckedChange={onToggle}
          className="bg-white shadow-sm"
        />
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center py-8">
      <div className="text-center">
        <div className="mx-auto mb-2 h-8 w-8 animate-spin rounded-full border-[hsl(var(--color))] border-b-2" />
        <p className="text-gray-500 text-sm">Loading wishlist items...</p>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="py-8 text-center">
      <div className="mb-4 text-gray-400">
        <svg
          className="mx-auto h-12 w-12"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <title>No Items</title>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
          />
        </svg>
      </div>
      <p className="mb-1 font-medium text-gray-900 text-lg">
        No items available
      </p>
      <p className="text-gray-500 text-sm">
        Your wishlist is empty or all items are already on this board
      </p>
    </div>
  );
}

export function AddItemsToBoardDialog({
  board,
  onItemsAdded,
  isOpen: controlledOpen,
  onOpenChange,
  children,
  primaryButtonClassName,
  secondaryButtonClassName,
  wishlistCard,
}: AddItemsToBoardDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(
    new Set()
  );

  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  const { items, isLoading, error } = useWishlistItemsWithProducts();
  const { linkItemToBoardMutation } = useWishlistMutations();

  // Filter out items that are already on this board (by product/variant combination)
  const availableItems = items.filter((item) => {
    // Create a set of product+variant combinations that are already on this board
    const boardProductVariants = new Set(
      board.items?.map(
        (boardItem) => `${boardItem.productId}-${boardItem.variantId || 'null'}`
      ) || []
    );

    // Check if this item's product+variant combination is already on the board
    const itemKey = `${item.productId}-${item.variantId || 'null'}`;
    return !boardProductVariants.has(itemKey);
  });

  const handleToggleItem = (itemId: string, selected: boolean) => {
    setSelectedItemIds((prev) => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(itemId);
      } else {
        newSet.delete(itemId);
      }
      return newSet;
    });
  };

  const handleToggleAll = (checked: boolean) => {
    if (checked) {
      setSelectedItemIds(new Set(availableItems.map((item) => item.id)));
    } else {
      setSelectedItemIds(new Set());
    }
  };

  const handleAddItems = async () => {
    const selectedItems = availableItems.filter((item) =>
      selectedItemIds.has(item.id)
    );

    // Reset selection and close dialog
    setSelectedItemIds(new Set());
    setOpen(false);
    onItemsAdded?.();

    try {
      // Link all selected items to the board in parallel
      // Let the server response handle showing the items
      await Promise.all(
        selectedItems.map((item) =>
          linkItemToBoardMutation.mutateAsync({
            itemId: item.id,
            boardId: board.id,
          })
        )
      );
    } catch {
      // Error handling is done by the mutation and toasts
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setSelectedItemIds(new Set());
    }
  };

  // Reset selection when available items change (e.g., after optimistic updates)
  useEffect(() => {
    if (selectedItemIds.size > 0) {
      const stillAvailableIds = new Set(availableItems.map((item) => item.id));
      const updatedSelection = new Set(
        Array.from(selectedItemIds).filter((id) => stillAvailableIds.has(id))
      );
      if (updatedSelection.size !== selectedItemIds.size) {
        setSelectedItemIds(updatedSelection);
      }
    }
  }, [availableItems, selectedItemIds]);

  const isAllSelected =
    availableItems.length > 0 && selectedItemIds.size === availableItems.length;

  return (
    <ResponsiveDialog isOpen={open} onOpenChange={handleOpenChange}>
      {children && (
        <ResponsiveDialogTrigger asChild>{children}</ResponsiveDialogTrigger>
      )}
      <ResponsiveDialogContent
        className="max-w-2xl"
        title={`Add Items to ${board.name}`}
        description="Select items from your wishlist to add to this board."
      >
        <div className="pt-2">
          <div className="px-4">
            <div className="max-h-96 overflow-y-auto">
              {(() => {
                if (isLoading) {
                  return <LoadingState />;
                }

                if (error) {
                  return (
                    <div className="py-8 text-center">
                      <div className="mb-2 text-red-500">⚠️</div>
                      <p className="text-gray-600 text-sm">
                        Failed to load wishlist items
                      </p>
                      <p className="mt-1 text-gray-400 text-xs">
                        {error.message}
                      </p>
                    </div>
                  );
                }

                if (availableItems.length === 0) {
                  return <EmptyState />;
                }

                const WishlistCard = wishlistCard;

                return (
                  <div className="space-y-4">
                    {/* Select All */}
                    <div className="flex items-center space-x-3 rounded-lg border border-gray-300 bg-gray-50 p-3">
                      <Checkbox
                        id="select-all"
                        checked={isAllSelected}
                        onCheckedChange={handleToggleAll}
                        aria-describedby="select-all-description"
                      />
                      <label
                        htmlFor="select-all"
                        className="flex-1 cursor-pointer font-medium text-sm"
                      >
                        Select All ({availableItems.length} items)
                      </label>
                      <span id="select-all-description" className="sr-only">
                        Toggle selection for all {availableItems.length}{' '}
                        available wishlist items
                      </span>
                    </div>

                    {/* Items Grid */}
                    <div
                      className="grid grid-cols-2 gap-3 md:grid-cols-4"
                      aria-label="Available wishlist items for selection"
                    >
                      {availableItems.map((item) => (
                        <SelectableWishlistCard
                          key={item.id}
                          item={item}
                          isSelected={selectedItemIds.has(item.id)}
                          onToggle={(selected) =>
                            handleToggleItem(item.id, selected)
                          }
                          WishlistCard={WishlistCard}
                        />
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          <DialogFooter className="mt-6">
            <ResponsiveDialogClose asChild>
              <Button
                className={secondaryButtonClassName}
                disabled={linkItemToBoardMutation.isPending}
              >
                Cancel
              </Button>
            </ResponsiveDialogClose>
            <Button
              className={primaryButtonClassName}
              onClick={handleAddItems}
              disabled={
                availableItems.length === 0 ||
                selectedItemIds.size === 0 ||
                linkItemToBoardMutation.isPending
              }
            >
              {linkItemToBoardMutation.isPending
                ? 'Adding...'
                : `Add ${selectedItemIds.size} Item${selectedItemIds.size === 1 ? '' : 's'}`}
            </Button>
          </DialogFooter>
        </div>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
